import { useEffect, useRef, useState } from "react";
import { useBrowserPathname } from "../../hooks/useBrowserPathname";
import { initPromoCapture } from "../auth/promoCapture";
import { ForgotPasswordPage } from "../auth/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "../auth/pages/ResetPasswordPage";
import { SignInPage } from "../auth/pages/SignInPage";
import { SignUpPage } from "../auth/pages/SignUpPage";
import { VerifyEmailPage } from "../auth/pages/VerifyEmailPage";
import { navigateTo } from "../auth/components/authNavigation";
import { signOutAdminAuthSession } from "../auth/api/adminAuthApi";
import { AdminDashboardPage } from "../dashboard/AdminDashboardPage";
import { ClientWorkspacePage } from "../dashboard/client-workspace/ClientWorkspacePage";
import { ToastProvider } from "../dashboard/components/primitives/ToastProvider";
import { useDashboardBootstrap } from "../dashboard/hooks/useDashboardBootstrap";
import { DashboardVisualRegressionPage } from "../dashboard/visual-regression/DashboardVisualRegressionPage";
import { ClientWorkspaceVisualRegressionPage } from "../dashboard/visual-regression/ClientWorkspaceVisualRegressionPage";
import { SessionLoadingScreen } from "../auth/components/SessionLoadingScreen";
import type { DashboardRole } from "../dashboard/types";

async function signOutToSignIn(): Promise<string | null> {
  const result = await signOutAdminAuthSession();

  if (!result.ok) {
    return result.error.message;
  }

  navigateTo("/admin/sign-in");
  return null;
}

function DashboardLanding(props: {
  role: DashboardRole;
  canMutate: boolean;
  displayName: string;
}) {
  const [signOutError, setSignOutError] = useState<string | null>(null);

  async function handleSignOut() {
    setSignOutError(null);
    const error = await signOutToSignIn();
    if (error) {
      setSignOutError(error);
    }
  }

  // Role gate: render ClientWorkspacePage for client role.
  // ClientWorkspacePage (and its descendants like ProjectActionMenu and modals)
  // consume the Toast context via useToastContext(), so they require a
  // ToastProvider ancestor. AdminDashboardPage self-wraps in its own provider,
  // so the provider is scoped to the client branch only — wrapping above the
  // role branch would nest two providers on the admin path. See NODE-EXPRESS-N.
  if (props.role === 'client') {
    return (
      <ToastProvider>
        <ClientWorkspacePage
          role="client"
          displayName={props.displayName}
          onSignOut={handleSignOut}
          signOutError={signOutError}
        />
      </ToastProvider>
    );
  }

  return (
    <AdminDashboardPage
      canMutate={props.canMutate}
      displayName={props.displayName}
      onSignOut={handleSignOut}
      role={props.role}
      signOutError={signOutError}
      data-oid="a8mbtlz"
    />
  );
}

function SuperAdminAccessDenied() {
  return (
    <div className="auth-shell" data-oid="o8tev75">
      <div className="auth-card" data-oid="4-ekl8i">
        <div className="auth-card__content" data-oid="u0:myio">
          <div className="auth-badge" data-oid="1r_qb8h">
            Access Restricted
          </div>
          <h1 className="auth-title" data-oid="xusccd_">
            Superadmin access only.
          </h1>
          <p className="auth-subtitle" data-oid="phm6udf">
            This workspace is reserved for global admin operations such as
            managing admins, clients, and platform access policies.
          </p>
        </div>
      </div>
    </div>
  );
}

function WorkspaceAccessRequired(props: {
  onSignOut: () => Promise<string | null>;
}) {
  const [signOutError, setSignOutError] = useState<string | null>(null);

  async function handleSignOut() {
    setSignOutError(null);
    const error = await props.onSignOut();
    if (error) {
      setSignOutError(error);
    }
  }

  return (
    <div className="auth-shell" data-oid="wlg0d9h">
      <div className="auth-card" data-oid="tj8g4_k">
        <div className="auth-card__content" data-oid="ya.u5ck">
          <div className="auth-badge" data-oid="q8n3tb4">
            Access Required
          </div>
          <h1 className="auth-title" data-oid="3ewx3s7">
            Workspace access required.
          </h1>
          <p className="auth-subtitle" data-oid="wcmf39t">
            Your account is active, but dashboard entry stays blocked until you
            request access to an existing workspace and an admin grants it.
          </p>
          <p className="auth-helper-text" data-oid="d.-3l19">
            Contact your workspace admin to finish onboarding, or sign out and
            try again.
          </p>
          {signOutError ? (
            <p className="auth-helper-text" data-oid="5p-zvuo">
              {signOutError}
            </p>
          ) : null}
          <button
            className="auth-button"
            type="button"
            onClick={() => void handleSignOut()}
            data-oid="s3ypv7y"
          >
            <span data-oid="szh.zwh">Sign out</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminLanding() {
  const pathname = useBrowserPathname();
  const dashboardBootstrap = useDashboardBootstrap({ revalidateKey: pathname });
  const promoCaptureInitRef = useRef(false);

  useEffect(() => {
    if (dashboardBootstrap.isLoading || promoCaptureInitRef.current) return;
    promoCaptureInitRef.current = true;
    initPromoCapture(dashboardBootstrap.isAuthenticated);
  }, [dashboardBootstrap.isLoading, dashboardBootstrap.isAuthenticated]);

  if (dashboardBootstrap.isLoading) {
    return <SessionLoadingScreen />;
  }

  if (!dashboardBootstrap.isAuthenticated || !dashboardBootstrap.role) {
    return (
      <SignInPage
        sessionNotice={dashboardBootstrap.sessionNotice}
        data-oid="qlllu20"
      />
    );
  }

  if (dashboardBootstrap.requiresWorkspaceAccess) {
    return (
      <WorkspaceAccessRequired
        onSignOut={signOutToSignIn}
        data-oid="dc7t9lj"
      />
    );
  }

  return (
    <DashboardLanding
      canMutate={dashboardBootstrap.canMutate}
      displayName={dashboardBootstrap.displayName}
      role={dashboardBootstrap.role}
      data-oid="4a2pcfd"
    />
  );
}

function SuperAdminLanding() {
  const pathname = useBrowserPathname();
  const dashboardBootstrap = useDashboardBootstrap({ revalidateKey: pathname });

  if (dashboardBootstrap.isLoading) {
    return <SessionLoadingScreen />;
  }

  if (!dashboardBootstrap.isAuthenticated) {
    return (
      <SignInPage
        sessionNotice={dashboardBootstrap.sessionNotice}
        data-oid="l:cqm99"
      />
    );
  }

  if (!dashboardBootstrap.isSuperadmin || !dashboardBootstrap.role) {
    return <SuperAdminAccessDenied data-oid="-_euo6o" />;
  }

  return (
    <DashboardLanding
      canMutate={dashboardBootstrap.canMutate}
      displayName={dashboardBootstrap.displayName}
      role={dashboardBootstrap.role}
      data-oid="k2b:h98"
    />
  );
}

export function AdminRouter() {
  const pathname = useBrowserPathname();

  switch (pathname) {
    case "/admin/sign-in":
      return <SignInPage data-oid="5kvh5k-" />;
    case "/admin/sign-up":
      return <SignUpPage data-oid="33gsj9f" />;
    case "/admin/verify-email":
      return <VerifyEmailPage data-oid="x-qkzi_" />;
    case "/admin/forgot-password":
      return <ForgotPasswordPage data-oid="qme5vjn" />;
    case "/admin/reset-password":
      return <ResetPasswordPage data-oid="qx.pyds" />;
    case "/admin/visual-regression":
      return <DashboardVisualRegressionPage data-oid="lcz467x" />;
    case "/admin/client-workspace-visual-regression":
      return <ClientWorkspaceVisualRegressionPage />;
    case "/superadmin":
      return <SuperAdminLanding data-oid="dd_qj3b" />;
    case "/admin":
    default:
      return <AdminLanding data-oid="kyk_v4u" />;
  }
}
